import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Typography, 
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_ENDPOINTS } from '../../../../config/api';

// Custom event name for application settings type updates
const APP_SETTINGS_TYPE_UPDATED_EVENT = 'app_settings_type_updated';

const EditApplicationType = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get the ID from URL
  const [formData, setFormData] = useState({
    type: '',
    slug: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch the application type on component mount
  useEffect(() => {
    const fetchApplicationType = async () => {
      try {
        setFetchLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get(`${API_ENDPOINTS.APPLICATION_TYPES.BASE}/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const fetchedType = response.data.data;
        setFormData({
          type: fetchedType.type,
          slug: fetchedType.slug
        });
        setError(null);
      } catch (error) {
        console.error('Error fetching application type:', error);
        setError('Failed to fetch application type. ' + (error.response?.data?.message || ''));
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchApplicationType();
    }
  }, [id, navigate]);

  // Function to convert string to slug
  const convertToSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-');
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value,
      // If type field is changed, automatically update the slug
      ...(name === 'type' ? { slug: convertToSlug(value) } : {})
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }

      if (!formData.type) {
        setError('Type name is required');
        setLoading(false);
        return;
      }

      // Create application type data
      const applicationTypeData = { 
        type: formData.type,
        slug: formData.slug
      };

      // console.log('Updating application type:', applicationTypeData);
      
      // Use the EDIT endpoint from API_ENDPOINTS
      const response = await axios.put(API_ENDPOINTS.APPLICATION_TYPES.EDIT(id), applicationTypeData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSuccess(true);
        
        // Dispatch custom event to trigger sidebar refresh
        window.dispatchEvent(new CustomEvent(APP_SETTINGS_TYPE_UPDATED_EVENT));
        
        setTimeout(() => {
          navigate('/application-setting-types');
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating application type:', error);
      
      // More detailed error logging
      if (error.response) {
        console.error('Server response data:', error.response.data);
        console.error('Server response status:', error.response.status);
        
        // Display more informative error message
        if (error.response.data && error.response.data.errors) {
          // Laravel validation errors
          const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
          setError(`Validation error: ${errorMessages}`);
        } else if (error.response.data && error.response.data.message) {
          setError(error.response.data.message);
        } else {
          setError(`Server error (${error.response.status}). Please check server logs.`);
        }
      } else if (error.request) {
        console.error('No response received:', error.request);
        setError('No response from server. Please check if the API is running.');
      } else {
        console.error('Error setting up request:', error.message);
        setError(`Request setup error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            {/* <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate('/application-setting-types')} 
              sx={{ mr: 2 }}
            >
              Back to Application Types
            </Button>
            <Typography variant="h5">
              Edit Application Type
            </Typography> */}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Application type updated successfully!
            </Alert>
          )}

          {fetchLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Slug"
                    name="slug"
                    value={formData.slug}
                    InputProps={{
                      readOnly: true,
                    }}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Update Application Type'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => navigate('/application-setting-types')}
                      sx={{ ml: 2 }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default EditApplicationType;
